/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";

import { Box, Grid } from "@mui/material";
import { useField } from "formik";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { Input } from "components/haven-design/form/inputs";
import { SelectAutocomplete } from "components/haven-design/form/select";

export const PossiblePropManagerRemovalReasons = [
  "Accidentally Applied",
  "Credit Denied",
  "Criminal Background Denied",
  "Deceased",
  "Did not submit requested documents",
  "Duplicate application",
  "Found another home",
  "Fraud in application process",
  "Full time student household",
  "Justice involvement screening",
  "Looking for different housing type",
  "No longer interested",
  "No response to interview request",
  "No response to outreach",
  "No response to waitlist update",
  "No show to interview",
  "Not chosen in lottery",
  "Over Income",
  "Refused background check",
  "Requested to be removed",
  "Too expensive for applicant",
  "Turned down/skipped too many times",
  "Unable to accommodate ADA unit",
  "Unable to accommodate household size",
  "Unable to accommodate pets",
  "Unable to contact",
  "Under age limit",
  "Under Income",
  "Other",
];

export const PossibleClientRemovalReasons = [
  "No longer interested",
  "Too far away",
  "Don't like the neighborhood",
  "Too expensive",
  "Found other housing",
  "Not income qualified",
  "Want different unit size",
  "My household size doesn't fit the unit requirements",
  "Duplicate application",
  "Other",
];

export const PossibleWaitlistUpdateRemovalReasons = [
  "Found other housing",
  "Passed away",
  "Medical reasons",
  "Moved to another city",
  "No longer interested - I don't want to be on the list anymore",
  "This isn't my phone number or email and I don't want any more notifications",
  "Other",
];

export type StructuredReasonsProps = {
  label: string
  options?: string[]
  disabled?: boolean
  error?: boolean
  helperText?: string
  onChange: (reason: string) => void
}

const StructuredReasons: React.FC<StructuredReasonsProps> = ({
  disabled,
  onChange,
  options = PossiblePropManagerRemovalReasons,
  label,
  error,
  helperText,
}) => {
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState("");
  const [actionDate, setActionDate] = React.useState<Date | null>(new Date());

  const handleChange = () => {
    let fullReason = reason ?? "";
    const actionDateString = moment(actionDate).format("MM/DD/YYYY");
    if (reason === "Other") {
      fullReason = other ? `${reason} - ${other}` : "";
    }
    if (fullReason !== "") fullReason = `${actionDateString} - ${fullReason}`;
    onChange(fullReason);
  };

  useEffect(handleChange, [reason, other, actionDate]);

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DatePicker
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                autoComplete: "off",
                variant: "outlined",
                label: "Removal Date",
              },
              inputAdornment: { position: "end" },
            }}
            disableFuture
            closeOnSelect
            format="MM/dd/yyyy"
            value={actionDate}
            onChange={setActionDate}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <SelectAutocomplete
            label={label}
            disabled={disabled}
            error={error}
            helperText={helperText}
            options={options}
            value={reason}
            margin="none"
            onChange={(_, value) => setReason(value)} />
        </Grid>

        {reason === "Other" && (
          <Grid size={{ xs: 12 }}>
            <Input
              required
              name="other"
              label="Reason"
              disabled={disabled}
              value={other}
              onChange={(e) => setOther(e.target.value)} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export type FormikStructuredReasonsProps = {
  name: string
} & Omit<StructuredReasonsProps, "onChange">

export const FormikStructuredReasons: React.FC<FormikStructuredReasonsProps> = ({
  name,
  ...props
}) => {
  const [,, { setValue }] = useField<string>(name);

  return <StructuredReasons {...props} onChange={setValue} />;
};

export default StructuredReasons;
